import { ref, shallowRef, watch } from 'vue'
import { useRoute } from 'vue-router'
import { Haptics, ImpactStyle } from '@capacitor/haptics'
import { useLogsStore } from '@/store/logs'
import { useEventListener } from '@vueuse/core'

const globalRefreshHandler = shallowRef(null)

function setRefreshHandler(fn, hapticFeedback) {
  globalRefreshHandler.value = () => {
    fn().then(() => {
      if (hapticFeedback) {
        Haptics.impact({ style: ImpactStyle.Light }).catch(e => {
          useLogsStore().addLogEntry({ level: 'ERROR', message: `Haptics error: ${e.message}`, error: e, tag: 'pullToRefresh' })
        })
      }
    })
  }
}

export const PULL_HEIGHT = 140
export const PULL_THRESHOLD = 180

export function setupPullToRefresh() {
  const route = useRoute()

  const isRefreshing = ref(false)
  const refreshValue = ref(0)
  const pullRefreshElement = ref(null)
  let isPulling = false
  let startY = 0

  async function triggerRefresh() {
    if (isRefreshing.value) return
    isRefreshing.value = true

    if (pullRefreshElement.value) {
      pullRefreshElement.value.style.height = `${PULL_HEIGHT}px`
    }
    refreshValue.value = 100

    try {
      if (globalRefreshHandler.value) {
        await globalRefreshHandler.value()
      }
    } finally {
      if (pullRefreshElement.value) {
        pullRefreshElement.value.classList.add('fade-height')
        pullRefreshElement.value.style.height = '0px'
      }
      isRefreshing.value = false
      refreshValue.value = 0
      isPulling = false
    }
  }

  function onTouchStart(e) {
    if (!globalRefreshHandler.value || !pullRefreshElement.value) {
      isPulling = false
      return
    }

    const scrollTop = document.scrollingElement.scrollTop
    if (scrollTop <= 5 && !isRefreshing.value) {
      pullRefreshElement.value.classList.remove('fade-height')
      startY = e.touches[0].clientY
      isPulling = true
      return
    }
    isPulling = false
  }

  async function onTouchMove(e) {
    if (!isPulling || !pullRefreshElement.value) return

    const currentY = e.touches[0].clientY
    const deltaY = currentY - startY
    // If user drags upward → stop pull mode and allow scroll
    if (deltaY <= 0) {
      pullRefreshElement.value.style.height = '0px'
      refreshValue.value = 0
      isPulling = false
      return
    }

    if (e.cancelable) e.preventDefault()

    if (deltaY < PULL_THRESHOLD) {
      const pullAmount = Math.min(deltaY, PULL_HEIGHT)
      pullRefreshElement.value.style.height = `${pullAmount}px`
      refreshValue.value = Math.min((deltaY / PULL_THRESHOLD) * 100, 100)
    }

    if (deltaY >= PULL_THRESHOLD) {
      isPulling = false
      refreshValue.value = 100
      await triggerRefresh()
    }
  }

  function onTouchEnd() {
    if (!isPulling || isRefreshing.value || !pullRefreshElement.value) return

    pullRefreshElement.value.style.height = '0px'
    refreshValue.value = 0
    isPulling = false
  }

  useEventListener(window, 'touchstart', onTouchStart)
  useEventListener(window, 'touchmove', onTouchMove, { passive: false })
  useEventListener(window, 'touchend', onTouchEnd)

  watch(
    () => route.name,
    () => {
      // disable for new route by default
      globalRefreshHandler.value = null
    }
  )

  return {
    isRefreshing,
    refreshValue,
    pullRefreshElement,
  }
}

export function usePullToRefresh() {
  return {
    setRefreshHandler,
  }
}
